import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, Phone, MapPin, Heart, Lock, MessagesSquare,
  Users, AlertTriangle, ShieldCheck
} from "lucide-react";

export default function SitterFamilyView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/families/${encodeURIComponent(userId)}/profile`)
      .then(({ data }) => setData(data))
      .catch((e) => { const m = extractError(e); setError(m); toast.error(m); });
  }, [userId]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-14">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="mt-6 paper-card p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto text-danger" />
            <p className="mt-3 text-sage-900 font-heading font-semibold">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="p-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-sage-600" /></div>
      </div>
    );
  }

  const { connected, display_name, city, state, zip_code, address, email, phone,
          household_notes, children, emergency_contacts, pets } = data;
  const location = [city, state, zip_code].filter(Boolean).join(", ") || "—";

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="sitter-family-view">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-3 paper-card p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
              {display_name[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-heading font-bold text-sage-900" data-testid="sitter-family-name">{display_name}</h1>
              <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-stone2-500">
                <MapPin className="w-3.5 h-3.5" /> {location}
              </div>
            </div>
          </div>

          {/* Connection state banner */}
          {!connected ? (
            <div className="mt-4 rounded-xl bg-ochre-50 border border-ochre-200 p-3 text-xs text-stone2-800 leading-relaxed flex items-start gap-2"
              data-testid="sitter-family-locked-banner">
              <Lock className="w-4 h-4 text-ochre-700 shrink-0 mt-0.5" />
              <div>
                <strong className="text-sage-900">Limited view.</strong> Contact details, full address,
                and emergency contacts unlock once the family accepts a booking with you.
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-xl bg-sage-50 border border-sage-200 p-3 text-xs text-stone2-800 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-sage-600 shrink-0 mt-0.5" />
              <div><strong className="text-sage-900">Full view unlocked</strong> — you have an active booking with this family.</div>
            </div>
          )}

          {connected && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              {email && (
                <a href={`mailto:${email}`} className="inline-flex items-center gap-2 text-sage-700 hover:text-sage-900" data-testid="sitter-family-email">
                  <Mail className="w-4 h-4" /> {email}
                </a>
              )}
              {phone && (
                <a href={`tel:${phone}`} className="inline-flex items-center gap-2 text-sage-700 hover:text-sage-900" data-testid="sitter-family-phone">
                  <Phone className="w-4 h-4" /> {phone}
                </a>
              )}
              {address && (
                <div className="col-span-2 text-stone2-700"><strong className="text-sage-900">Address:</strong> {address}</div>
              )}
            </div>
          )}

          <div className="mt-5">
            <Link to="/messages" className="btn-primary text-sm">
              <MessagesSquare className="w-4 h-4" /> Message this family
            </Link>
          </div>
        </div>

        {household_notes && (
          <section className="mt-6 paper-card p-6">
            <h2 className="font-heading font-semibold text-sage-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-sage-600" /> Household notes
            </h2>
            <p className="mt-2 text-sm text-stone2-700 leading-relaxed">{household_notes}</p>
          </section>
        )}

        {children.length > 0 && (
          <section className="mt-6 paper-card p-6" data-testid="sitter-family-children">
            <h2 className="font-heading font-semibold text-sage-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-sage-600" /> Children in your care ({children.length})
            </h2>
            <ul className="mt-4 space-y-3">
              {children.map((c) => (
                <li key={c.id} className="rounded-xl border border-stone2-200 bg-white p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-sage-900">{c.name || "Child"}</span>
                    {c.age !== null && c.age !== undefined && <span className="chip">{c.age} yrs</span>}
                    {c.diagnosis && <span className="chip-ochre">{c.diagnosis}</span>}
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                    {c.triggers && <div><strong className="text-sage-900">Triggers:</strong> <span className="text-stone2-700">{c.triggers}</span></div>}
                    {c.calming_strategies && <div><strong className="text-sage-900">Calming strategies:</strong> <span className="text-stone2-700">{c.calming_strategies}</span></div>}
                    {c.interests && <div><strong className="text-sage-900">Interests:</strong> <span className="text-stone2-700">{c.interests}</span></div>}
                    {c.communication_style && <div><strong className="text-sage-900">Communication:</strong> <span className="text-stone2-700">{c.communication_style}</span></div>}
                    {c.allergies && <div><strong className="text-sage-900">Allergies:</strong> <span className="text-stone2-700">{c.allergies}</span></div>}
                    {c.medications && <div><strong className="text-sage-900">Medications:</strong> <span className="text-stone2-700">{c.medications}</span></div>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(emergency_contacts.length > 0 || pets.length > 0) && (
          <section className="mt-6 grid sm:grid-cols-2 gap-6">
            {emergency_contacts.length > 0 && (
              <div className="paper-card p-6">
                <h3 className="font-heading font-semibold text-sage-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-sage-600" /> Emergency contacts
                </h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {emergency_contacts.map((c, i) => (
                    <li key={c._uid || i}>
                      <div className="font-semibold text-sage-900">{c.name} <span className="text-stone2-500 font-normal">· {c.relationship}</span></div>
                      <div className="text-xs text-stone2-600">
                        {c.phone && <a href={`tel:${c.phone}`} className="text-sage-700 mr-3">{c.phone}</a>}
                        {c.email && <a href={`mailto:${c.email}`} className="text-sage-700">{c.email}</a>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pets.length > 0 && (
              <div className="paper-card p-6">
                <h3 className="font-heading font-semibold text-sage-900">Pets</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {pets.map((p, i) => (
                    <li key={p._uid || i} className="text-sage-900">
                      <strong>{p.name || "Unnamed"}</strong> <span className="text-stone2-500">· {p.type}</span>
                      {p.notes && <div className="text-xs text-stone2-600">{p.notes}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
